import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Star, IdCard, Lock, Mail } from "lucide-react";
import CinemaBrand from "@/components/CinemaBrand";

export default function MemberLogin() {
  const nav = useNavigate();
  const [tab, setTab] = useState("login"); // login | register
  const [ident, setIdent] = useState("");
  const [password, setPassword] = useState("");
  const [regMember, setRegMember] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const doLogin = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/member/login", { identifier: ident.trim(), password });
      // Fallback für Browser, die Third-Party-Cookies blockieren (Safari ITP, Incognito, prod ≠ preview)
      if (data?.token) localStorage.setItem("mh_member_token", data.token);
      toast.success("Willkommen zurück!");
      nav("/mitglied");
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Login fehlgeschlagen");
    } finally { setBusy(false); }
  };

  const doRegister = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await api.post("/member/register", {
        member_number: regMember.trim().toUpperCase(),
        email: regEmail.trim(),
        password: regPassword,
      });
      toast.success("Passwort gesetzt — bitte einloggen");
      setTab("login");
      setIdent(regMember.trim().toUpperCase());
      setPassword("");
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Registrierung fehlgeschlagen");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center px-4 overflow-hidden">
      <div className="absolute inset-0 -z-10">
        <img src="https://images.unsplash.com/photo-1705944601101-fdb49dbf884c?w=1920" alt=""
          className="w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/85 via-black/70 to-black" />
      </div>

      <div className="relative w-full max-w-md">
        <div className="text-center mb-8">
          <CinemaBrand size="xl" stacked />
          <div className="mt-3 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.4em] text-[#D4AF37]">
            <Star size={12} fill="#D4AF37" strokeWidth={0} /> Movie House Club
          </div>
        </div>

        <div className="card-lux p-7 gold-glow">
          {/* Tab-Umschalter */}
          <div className="flex mb-6 rounded-md border border-[#2A2A2A] p-1 text-[10px] uppercase tracking-[0.32em]">
            <button onClick={() => { setTab("login"); setErr(""); }} data-testid="tab-login"
              className={`flex-1 py-2 rounded transitions-safe ${tab === "login" ? "bg-[#D4AF37] text-black" : "text-[#A8A398]"}`}>
              Anmelden
            </button>
            <button onClick={() => { setTab("register"); setErr(""); }} data-testid="tab-register"
              className={`flex-1 py-2 rounded transitions-safe ${tab === "register" ? "bg-[#D4AF37] text-black" : "text-[#A8A398]"}`}>
              Passwort setzen
            </button>
          </div>

          {tab === "login" ? (
            <form onSubmit={doLogin} className="space-y-4">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#A8A398] flex items-center gap-1"><IdCard size={12} /> Kundennummer oder E-Mail</label>
                <input value={ident} onChange={(e) => setIdent(e.target.value)} data-testid="member-login-identifier" required
                  placeholder="z.B. 2LC82N oder deine@email.de"
                  className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-3 focus:border-[#D4AF37] outline-none font-mono" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#A8A398] flex items-center gap-1"><Lock size={12} /> Passwort</label>
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="member-login-password" required
                  className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-3 focus:border-[#D4AF37] outline-none" />
              </div>
              {err && <div data-testid="member-login-error" className="text-sm text-[#EE9CA1] bg-[#5B1319]/30 border border-[#5B1319] rounded px-3 py-2">{err}</div>}
              <button type="submit" disabled={busy} data-testid="member-login-submit"
                className="w-full py-3 bg-[#D4AF37] hover:bg-[#EEDC82] text-black font-medium tracking-widest uppercase text-sm rounded transitions-safe disabled:opacity-50">
                {busy ? "…" : "Einlass"}
              </button>
              <div className="text-[10px] text-[#575148] text-center">
                Noch kein Passwort? → <button type="button" onClick={() => { setTab("register"); setErr(""); }} className="text-[#D4AF37] hover:underline">Jetzt setzen</button>
              </div>
            </form>
          ) : (
            <form onSubmit={doRegister} className="space-y-4">
              <div className="text-[11px] text-[#A8A398] leading-relaxed border border-[#D4AF37]/25 bg-[#D4AF37]/[0.04] rounded p-3">
                Du bist bereits Mitglied und hast eine Kundennummer erhalten? Setz hier dein Passwort.
                Die E-Mail muss zu der bei der Anmeldung hinterlegten passen.
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#A8A398] flex items-center gap-1"><IdCard size={12} /> Kundennummer</label>
                <input value={regMember} onChange={(e) => setRegMember(e.target.value.toUpperCase())} data-testid="member-register-number" required
                  placeholder="AB12CD"
                  className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-3 focus:border-[#D4AF37] outline-none font-mono uppercase tracking-widest" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#A8A398] flex items-center gap-1"><Mail size={12} /> E-Mail</label>
                <input type="email" value={regEmail} onChange={(e) => setRegEmail(e.target.value)} data-testid="member-register-email" required
                  className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-3 focus:border-[#D4AF37] outline-none" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#A8A398] flex items-center gap-1"><Lock size={12} /> Neues Passwort (min. 6 Zeichen)</label>
                <input type="password" value={regPassword} onChange={(e) => setRegPassword(e.target.value)} data-testid="member-register-password" required minLength={6}
                  className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-3 focus:border-[#D4AF37] outline-none" />
              </div>
              {err && <div data-testid="member-register-error" className="text-sm text-[#EE9CA1] bg-[#5B1319]/30 border border-[#5B1319] rounded px-3 py-2">{err}</div>}
              <button type="submit" disabled={busy} data-testid="member-register-submit"
                className="w-full py-3 border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10 font-medium tracking-widest uppercase text-sm rounded transitions-safe disabled:opacity-50">
                {busy ? "…" : "Passwort setzen"}
              </button>
            </form>
          )}
        </div>

        <div className="mt-6 text-center text-xs text-[#575148]">
          <Link to="/" className="hover:text-[#D4AF37] transitions-safe">← Zurück zur Startseite</Link>
          <span className="mx-3">·</span>
          <Link to="/login" className="hover:text-[#D4AF37] transitions-safe">Mitarbeiter-Login</Link>
        </div>
      </div>
    </div>
  );
}
